'use client';
import { useEffect, useState } from 'react';
/**
 * Zustand Devtools component for development
 *
 * Works with stores that have the devtools middleware enabled.
 * Requires the Redux DevTools browser extension.
 *
 * To enable devtools in your Zustand stores, wrap them with the devtools middleware:
 *
 * @example
 * ```tsx
 * import { create } from 'zustand';
 * import { devtools } from 'zustand/middleware';
 *
 * const useStore = create(
 *   devtools(
 *     (set) => ({
 *       count: 0,
 *       increment: () => set((state) => ({ count: state.count + 1 }), undefined, 'increment'),
 *     }),
 *     { name: 'MyStore' }
 *   )
 * );
 * ```
 *
 * Install Redux DevTools extension:
 * - Chrome: https://chrome.google.com/webstore/detail/redux-devtools/lmhkpmbekcpmknklioeibfkpmmfibljd
 * - Firefox: https://addons.mozilla.org/en-US/firefox/addon/reduxdevtools/
 */
export function ZustandDevtools({ enabled }) {
    const shouldLoad = enabled ?? process.env.NODE_ENV === 'development';
    const [_hasReduxDevtools, setHasReduxDevtools] = useState(false);
    useEffect(() => {
        if (!shouldLoad) {
            return;
        }
        // Check if Redux DevTools extension is installed
        const checkExtension = () => {
            if (typeof window !== 'undefined') {
                const win = window;
                const hasExtension = !!win.__REDUX_DEVTOOLS_EXTENSION__;
                setHasReduxDevtools(hasExtension);
                if (hasExtension) {
                    console.log('[ZustandDevtools] ✓ Redux DevTools extension detected');
                    console.log('[ZustandDevtools] Stores with devtools middleware will appear in the extension');
                }
                else {
                    console.warn('[ZustandDevtools] Redux DevTools extension not found');
                    console.warn('[ZustandDevtools] Install from: https://github.com/reduxjs/redux-devtools');
                }
            }
        };
        checkExtension();
        // Check again after a delay in case extension loads late
        const timeout = setTimeout(checkExtension, 1000);
        return () => clearTimeout(timeout);
    }, [shouldLoad]);
    return null;
}
